/**
 * Read-only Effective Measurement Definition client.
 * Frontend consumes proven deployed geometry only. It does not invent polygons.
 */
import { api } from './api';

export type EffectiveMeasurementGeometry = {
  deployed?: boolean;
  geometry_type?: string | null;
  polygon_norm?: number[][] | null;
  bbox_norm?: number[] | null;
  coordinate_space?: string | null;
};

export type EffectiveMeasurementDefinition = {
  ok?: boolean;
  condition?: {
    condition_id?: string | null;
    condition_family?: string | null;
    condition_label?: string | null;
  };
  operational_scope?: {
    area_id?: string | null;
    area_name?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
    camera_name?: string | null;
  };
  measurement?: {
    scope_type?: string | null;
    scope_name?: string | null;
    scope_zone_id?: string | null;
    effective_geometry?: EffectiveMeasurementGeometry | null;
  };
  provenance?: {
    geometry_source?: string | null;
    limitations?: string[] | null;
  };
};

export type EffectiveMeasurementDefinitionResult =
  | { kind: 'ok'; data: EffectiveMeasurementDefinition }
  | { kind: 'http'; status: number; error: string | null }
  | { kind: 'network'; message: string };

export function effectiveMeasurementDefinitionPath(args: {
  siteId: string;
  conditionId: string;
}): string {
  return (
    `/api/v1/sites/${encodeURIComponent(args.siteId)}` +
    `/conditions/${encodeURIComponent(args.conditionId)}` +
    `/effective-measurement`
  );
}

function readErrorField(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

export async function getEffectiveMeasurementDefinition(args: {
  siteId: string;
  conditionId: string;
}): Promise<EffectiveMeasurementDefinitionResult> {
  try {
    const response = await api.get(
      effectiveMeasurementDefinitionPath({
        siteId: args.siteId,
        conditionId: args.conditionId,
      }),
    );
    return { kind: 'ok', data: response.data as EffectiveMeasurementDefinition };
  } catch (err) {
    const axiosLike = err as {
      message?: string;
      response?: { status?: number; data?: unknown };
    };
    const status = axiosLike.response?.status;
    if (typeof status === 'number') {
      return {
        kind: 'http',
        status,
        error: readErrorField(axiosLike.response?.data, 'error'),
      };
    }
    return { kind: 'network', message: axiosLike.message || 'network_error' };
  }
}
